/**
 * The User 360 — a descriptor, not a screen.
 *
 * The second entity to use the generic Entity 360, which is the real test of it: the activity timeline and
 * the audit trail below come free, keyed on `iam.user_accounts`, with nothing written for them here. What
 * IS written here is only what is particular to an account — its status, its roles and their scopes, its
 * applications, its resolved access and its sign-in history.
 *
 * It is a USER ACCOUNT 360, not a Person 360. The person, if there is one, is a link in the header to a
 * different record with a different lifecycle. An account can be suspended while the employee carries on
 * working, and a person can exist for years without ever having a login.
 *
 * Every button here is courtesy. The command functions check the permission, the privilege ceiling and the
 * state machine again, so where an action is unavailable the tab says *why* rather than hiding it.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, KeyRound, LockOpen, LogOut, Plus, RotateCcw, ShieldCheck, ShieldOff, UserX } from 'lucide-react';
import { rpc, supabase } from '../../lib/supabase';
import { useSession } from '../../lib/session';
import {
  Button, EmptyState, ErrorState, Fact, FactGrid, LoadingRows, Panel, PanelHeader, SectionLabel, StatusBadge,
  Unknown, cx,
} from '../../ui/primitives';
import { ConfirmDialog } from '../../ui/overlays';
import type { Entity360Descriptor } from '../../entity360/types';
import { AccessCheckPanel, EffectiveAccessView } from './AccessPreview';
import { GrantRoleDialog } from './GrantRoleDialog';
import { ACCOUNT_STATUS, when, type AccountStatus } from './types';
import { date as fmtDate, dateTime as fmtDateTime } from '../../ui/format';

export interface User360 {
  entity_type: string;
  id: string;
  code: string;
  email: string;
  name: string;
  status: AccountStatus;
  mfa_required: boolean;
  signs_in: boolean;
  last_sign_in_at: string | null;
  deactivated_at: string | null;
  created_at: string;
  updated_at: string;
  person: { id: string; display_name: string; status: string } | null;
  roles: {
    grant_id: string; code: string; name: string; scope: 'ORGANIZATION' | 'LOCATIONS';
    location_count: number; valid_to: string | null; requires_mfa: boolean;
  }[];
  applications: { code: string; name: string; status: string; authority: 'ERP' | 'MIRROR' }[];
  invitation: { id: string; status: string; expires_at: string; proposed_roles: string[] } | null;
  /** `null` means "you may not see this", which is not the same as zero. */
  counts: { grants: number; sign_ins: number | null; failures: number | null; events: number | null; applications: number };
}

function useRefresh(accountId: string) {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['users', '360', accountId] }),
      queryClient.invalidateQueries({ queryKey: ['iam-effective-access', accountId] }),
      queryClient.invalidateQueries({ queryKey: ['iam-users'] }),
      queryClient.invalidateQueries({ queryKey: ['entity-timeline'] }),
    ]);
  };
}

/* ── Overview: identity, and the state machine ───────────────────────────────────────────────────── */

type StatusAction = { to: AccountStatus; label: string; icon: typeof Ban; destructive: boolean; consequence: string };

const TRANSITIONS: Record<AccountStatus, StatusAction[]> = {
  INVITED: [],
  ACTIVE: [
    { to: 'SUSPENDED', label: 'Suspend', icon: Ban, destructive: true,
      consequence: 'The account stops resolving any permission on its very next request — there is no token to wait out. Its grants are kept, so restoring it puts everything back exactly as it was.' },
    { to: 'DEACTIVATED', label: 'Deactivate', icon: UserX, destructive: true,
      consequence: 'This is permanent. Every role grant is ended, every application access is revoked, and the account cannot be reopened — to give this person access again they must be invited afresh. The record itself is kept.' },
  ],
  SUSPENDED: [
    { to: 'ACTIVE', label: 'Restore', icon: RotateCcw, destructive: false,
      consequence: 'The account resolves its grants again immediately, exactly as they were before it was suspended.' },
    { to: 'DEACTIVATED', label: 'Deactivate', icon: UserX, destructive: true,
      consequence: 'This is permanent. Every role grant is ended, every application access is revoked, and the account cannot be reopened.' },
  ],
  LOCKED: [
    { to: 'ACTIVE', label: 'Unlock', icon: LockOpen, destructive: false,
      consequence: 'The account can sign in again and its failed-attempt count starts from zero. Confirm out of band that it really is them before unlocking: a lockout is sometimes someone else guessing.' },
    { to: 'DEACTIVATED', label: 'Deactivate', icon: UserX, destructive: true,
      consequence: 'This is permanent. Every role grant is ended and the account cannot be reopened.' },
  ],
  DEACTIVATED: [],
};

function UserOverview({ entity }: { entity: User360 }) {
  const { can, me } = useSession();
  const refresh = useRefresh(entity.id);
  const [pending, setPending] = useState<StatusAction | null>(null);
  const [mfaChange, setMfaChange] = useState<boolean | null>(null);
  const [endingSessions, setEndingSessions] = useState(false);
  const isSelf = me?.user_account_id === entity.id;
  const mayManage = can('iam.users.manage');

  const setStatus = useMutation({
    mutationFn: ({ status, reason }: { status: AccountStatus; reason: string }) =>
      rpc('set_user_status', { p_user_account_id: entity.id, p_status: status, p_reason: reason }),
    onSuccess: refresh,
  });
  const setMfa = useMutation({
    mutationFn: ({ required, reason }: { required: boolean; reason: string }) =>
      rpc('set_user_mfa_required', { p_user_account_id: entity.id, p_required: required, p_reason: reason }),
    onSuccess: refresh,
  });

  const endSessions = useMutation({
    mutationFn: ({ reason }: { reason: string }) =>
      rpc('revoke_user_sessions', { p_user_account_id: entity.id, p_reason: reason }),
    onSuccess: refresh,
  });

  const actions = TRANSITIONS[entity.status];
  const mfaRoles = entity.roles.filter((r) => r.requires_mfa).map((r) => r.name);

  return (
    <div>
      <FactGrid columns={4}>
        <Fact label="Email">{entity.email}</Fact>
        <Fact label="Person">
          {entity.person ? (
            <Link to={`/workforce/people/${entity.person.id}`} className="text-brand hover:underline">
              {entity.person.display_name}
            </Link>
          ) : (
            <Unknown title="This login is not linked to a person record" />
          )}
        </Fact>
        <Fact label="Signs in">{entity.signs_in ? 'Yes — bound to a sign-in identity' : 'Not yet — invitation not accepted'}</Fact>
        <Fact label="Last sign-in">{when(entity.last_sign_in_at) ?? <Unknown title="Has never signed in" />}</Fact>
        <Fact label="Two-factor">{entity.mfa_required ? 'Required' : 'Not required'}</Fact>
        <Fact label="Created">{when(entity.created_at)}</Fact>
        <Fact label="Last changed">{when(entity.updated_at)}</Fact>
        <Fact label="Deactivated">{when(entity.deactivated_at) ?? <Unknown title="Not deactivated" />}</Fact>
      </FactGrid>

      {!entity.mfa_required && mfaRoles.length > 0 ? (
        <div className="border-t border-admin/30 bg-admin-ground px-4 py-3 text-small text-admin">
          <span className="font-medium">Two-factor finding.</span> This account holds {mfaRoles.join(', ')}, which{' '}
          {mfaRoles.length === 1 ? 'is' : 'are'} defined as requiring two-factor authentication, but the account does not
          require it. A role’s flag is policy; the account’s setting is what is enforced.
        </div>
      ) : null}

      <div className="border-t border-line px-4 py-3">
        <SectionLabel>Account status</SectionLabel>
        {!mayManage ? (
          <p className="mt-1 text-small text-ink-muted">Changing an account needs <code className="code-text">iam.users.manage</code>.</p>
        ) : isSelf ? (
          // Saying why beats hiding the buttons. The case that matters is the reverse one: a suspended
          // administrator un-suspending themselves.
          <p className="mt-1 text-small text-ink-muted">
            This is your own account. Nobody changes their own status — ask another administrator.
          </p>
        ) : entity.status === 'INVITED' ? (
          <p className="mt-1 text-small text-ink-muted">
            An invited account becomes active when the invitation is accepted, not from here. Manage the invitation in{' '}
            <Link to="/admin/invitations" className="text-brand hover:underline">Invitations</Link>.
          </p>
        ) : entity.status === 'DEACTIVATED' ? (
          <p className="mt-1 text-small text-ink-muted">
            Deactivation is permanent. To give this person access again, invite them afresh.
          </p>
        ) : (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {actions.map((action) => (
              <Button key={action.to} size="dense" variant={action.destructive ? 'danger' : 'secondary'} onClick={() => setPending(action)}>
                <action.icon size={13} aria-hidden />
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-line px-4 py-3">
        <SectionLabel>Two-factor authentication</SectionLabel>
        <p className="mt-1 max-w-prose text-small text-ink-muted">
          {entity.mfa_required
            ? 'Required. Until a session passes its second factor this account holds no permission at all, and every command refuses with RTC_MFA_REQUIRED.'
            : 'Not required. A password alone is enough to use every permission this account holds.'}
        </p>
        {mayManage && !['DEACTIVATED'].includes(entity.status) ? (
          <div className="mt-2">
            {entity.mfa_required ? (
              isSelf ? (
                <p className="text-small text-ink-muted">You cannot remove your own two-factor requirement.</p>
              ) : (
                <Button size="dense" variant="danger" onClick={() => setMfaChange(false)}>
                  <ShieldOff size={13} aria-hidden /> Release the requirement
                </Button>
              )
            ) : (
              <Button size="dense" onClick={() => setMfaChange(true)}>
                <ShieldCheck size={13} aria-hidden /> Require two-factor
              </Button>
            )}
          </div>
        ) : null}
      </div>

      <div className="border-t border-line px-4 py-3">
        <SectionLabel>Sessions</SectionLabel>
        <p className="mt-1 max-w-prose text-small text-ink-muted">
          Ending this account’s sessions signs it out of every browser at its next request. The account stays as it is: the
          person signs in again. Suspending or deactivating an account ends its sessions as well.
        </p>
        {mayManage && entity.signs_in && entity.status === 'ACTIVE' ? (
          <div className="mt-2">
            <Button size="dense" variant="secondary" onClick={() => setEndingSessions(true)}>
              <LogOut size={13} aria-hidden /> End all sessions
            </Button>
          </div>
        ) : null}
      </div>

      <ConfirmDialog
        open={endingSessions}
        onClose={() => setEndingSessions(false)}
        title={`End every session of ${entity.email}`}
        consequence={isSelf
          ? 'This is your own account: you will be signed out here as well, and sign in again.'
          : 'Every browser this account is signed in on stops working at its next request. Nothing else about the account changes.'}
        confirmLabel="End sessions"
        requireReason
        onConfirm={async (reason) => {
          await endSessions.mutateAsync({ reason: reason ?? '' });
        }}
      />

      <ConfirmDialog
        open={pending !== null}
        onClose={() => setPending(null)}
        title={`${pending?.label ?? ''} ${entity.email}`}
        consequence={pending?.consequence}
        confirmLabel={pending?.label ?? 'Confirm'}
        destructive={pending?.destructive}
        requireReason
        onConfirm={async (reason) => {
          if (pending) await setStatus.mutateAsync({ status: pending.to, reason: reason ?? '' });
        }}
      />

      <ConfirmDialog
        open={mfaChange !== null}
        onClose={() => setMfaChange(null)}
        title={mfaChange ? 'Require two-factor authentication' : 'Release the two-factor requirement'}
        consequence={
          mfaChange
            ? 'From their next request this account holds no permission until the session passes a second factor. If they have not enrolled an authenticator yet, they will be walked through it the next time they open the ERP.'
            : 'A password alone will be enough to use every permission this account holds. Do this only when the requirement was set in error.'
        }
        confirmLabel={mfaChange ? 'Require' : 'Release'}
        destructive={mfaChange === false}
        requireReason
        onConfirm={async (reason) => {
          if (mfaChange !== null) await setMfa.mutateAsync({ required: mfaChange, reason: reason ?? '' });
        }}
      />

      {setStatus.isError || setMfa.isError || endSessions.isError ? <ErrorState error={setStatus.error ?? setMfa.error ?? endSessions.error} /> : null}
    </div>
  );
}

/* ── Roles: a role, in a scope, for a period ─────────────────────────────────────────────────────── */

function UserRoles({ entity }: { entity: User360 }) {
  const { can } = useSession();
  const refresh = useRefresh(entity.id);
  const [granting, setGranting] = useState(false);
  const [revoking, setRevoking] = useState<User360['roles'][number] | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const mayGrant = can('iam.grants.manage');

  const revoke = useMutation({
    mutationFn: ({ grantId, reason }: { grantId: string; reason: string }) =>
      rpc('revoke_role', { p_grant_id: grantId, p_reason: reason }),
    onSuccess: refresh,
  });

  return (
    <div>
      <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2">
        <SectionLabel>{entity.roles.length} current role{entity.roles.length === 1 ? '' : 's'}</SectionLabel>
        {mayGrant && entity.status !== 'DEACTIVATED' ? (
          <Button size="dense" variant="primary" onClick={() => setGranting(true)}>
            <Plus size={13} aria-hidden /> Grant a role
          </Button>
        ) : (
          <span className="text-small text-ink-muted">
            {entity.status === 'DEACTIVATED' ? 'A deactivated account cannot be granted roles' : 'Granting needs iam.grants.manage'}
          </span>
        )}
      </div>

      {warnings.map((warning) => (
        <p key={warning} className="border-b border-admin/30 bg-admin-ground px-4 py-2 text-small text-admin">{warning}</p>
      ))}

      {entity.roles.length === 0 ? (
        <EmptyState
          title="No current role"
          detail="This account can sign in and will see nothing. Ended grants are kept and appear under Access."
        />
      ) : (
        <ul className="divide-y divide-line">
          {entity.roles.map((role) => (
            <li key={role.grant_id} className="flex items-center justify-between gap-4 px-4 py-2.5">
              <div className="min-w-0">
                <span className="text-body">{role.name}</span>{' '}
                <span className="code-text text-ink-muted">{role.code}</span>
                <p className="mt-0.5 text-small text-ink-muted">
                  {role.scope === 'ORGANIZATION' ? 'Organisation-wide' : `${role.location_count} location${role.location_count === 1 ? '' : 's'}, and everything below them`}
                  {role.valid_to ? ` · until ${fmtDate(role.valid_to)}` : ' · open-ended'}
                  {role.requires_mfa ? ' · defined as requiring MFA' : ''}
                </p>
              </div>
              {mayGrant ? (
                <Button size="dense" variant="danger" onClick={() => setRevoking(role)}>
                  Revoke
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {revoke.isError ? <ErrorState error={revoke.error} /> : null}

      <GrantRoleDialog
        open={granting}
        onClose={() => setGranting(false)}
        accountId={entity.id}
        accountLabel={entity.email}
        onGranted={(result) => setWarnings(result.warnings ?? [])}
      />

      <ConfirmDialog
        open={revoking !== null}
        onClose={() => setRevoking(null)}
        title={`Revoke ${revoking?.name ?? 'this role'}`}
        consequence={
          <>
            The grant is closed from this moment and stops resolving on the account’s very next request. It is
            not deleted: who held what, and until when, is the point of the record.
          </>
        }
        confirmLabel="Revoke"
        destructive
        requireReason
        onConfirm={async (reason) => {
          if (revoking) await revoke.mutateAsync({ grantId: revoking.grant_id, reason: reason ?? '' });
        }}
      />
    </div>
  );
}

/* ── Applications ────────────────────────────────────────────────────────────────────────────────── */

interface ApplicationRow {
  id: string;
  code: string;
  name: string;
  owner_system: string;
  status: string;
}

function UserApplications({ entity }: { entity: User360 }) {
  const { can, me } = useSession();
  const isSelf = me?.user_account_id === entity.id;
  const refresh = useRefresh(entity.id);
  const mayManage = can('iam.applications.manage') && entity.status !== 'DEACTIVATED';
  const [change, setChange] = useState<{ app: ApplicationRow; status: 'ACTIVE' | 'SUSPENDED' | 'REVOKED' } | null>(null);

  const apps = useQuery({
    queryKey: ['iam-applications'],
    queryFn: async () => {
      const { data, error } = await supabase.from('applications').select('id,code,name,owner_system,status').order('code');
      if (error) throw error;
      return data as ApplicationRow[];
    },
  });

  const setAccess = useMutation({
    mutationFn: ({ code, status, reason }: { code: string; status: string; reason: string }) =>
      rpc('set_application_access', {
        p_user_account_id: entity.id, p_application_code: code, p_status: status, p_external_role_key: null, p_reason: reason,
      }),
    onSuccess: refresh,
  });

  if (apps.isLoading) return <LoadingRows rows={4} columns={3} />;
  if (apps.isError) return <ErrorState error={apps.error} retry={() => void apps.refetch()} />;

  const held = new Map(entity.applications.map((a) => [a.code, a]));

  return (
    <div>
      <p className="border-b border-line px-4 py-2 text-small text-ink-muted">
        Access to <span className="font-medium">RTCOM ERP</span> is the sign-in guard: without it this account resolves no
        permission here, whatever roles it holds. Only the ERP’s own applications are ERP-authoritative. A field application keeps its own access list, so what
        is recorded against one here is a <span className="font-medium">mirror</span> of it, labelled as such — the
        server derives that from the application, and nothing on this screen can claim otherwise.
      </p>
      <ul className="divide-y divide-line">
        {(apps.data ?? []).map((app) => {
          const access = held.get(app.code);
          const status = access?.status ?? 'NONE';
          return (
            <li key={app.id} className="flex items-center justify-between gap-4 px-4 py-2.5">
              <div className="min-w-0">
                <span className="text-body">{app.name}</span> <span className="code-text text-ink-muted">{app.code}</span>
                <p className="mt-0.5 text-small text-ink-muted">
                  {app.owner_system === 'ERP' ? 'ERP-authoritative' : `Owned by ${app.owner_system} · recorded here as a mirror`}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className={cx('label-text', status === 'ACTIVE' ? 'text-accepted' : 'text-ink-faint')}>
                  {status === 'NONE' ? 'No access' : status}
                </span>
                {mayManage && isSelf && app.code === 'ERP' ? (
                  // The ERP row is the sign-in guard. Revoking your own would lock you out of this screen.
                  <span className="text-small text-ink-muted">Your own — ask another administrator</span>
                ) : mayManage ? (
                  status === 'ACTIVE' ? (
                    <Button size="dense" variant="danger" onClick={() => setChange({ app, status: 'REVOKED' })}>Revoke</Button>
                  ) : (
                    <Button size="dense" disabled={app.status !== 'ACTIVE'} onClick={() => setChange({ app, status: 'ACTIVE' })}>
                      <KeyRound size={13} aria-hidden /> Give access
                    </Button>
                  )
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>

      {setAccess.isError ? <ErrorState error={setAccess.error} /> : null}

      <ConfirmDialog
        open={change !== null}
        onClose={() => setChange(null)}
        title={`${change?.status === 'ACTIVE' ? 'Give access to' : 'Revoke access to'} ${change?.app.name ?? ''}`}
        consequence={
          change?.app.owner_system === 'ERP'
            ? 'This changes what the account may open in the ERP itself.'
            : `${change?.app.name} keeps its own access list. This records the ERP’s view of it as a mirror; it does not change what that application allows until an approved publish path exists.`
        }
        confirmLabel={change?.status === 'ACTIVE' ? 'Give access' : 'Revoke'}
        destructive={change?.status !== 'ACTIVE'}
        requireReason
        onConfirm={async (reason) => {
          if (change) await setAccess.mutateAsync({ code: change.app.code, status: change.status, reason: reason ?? '' });
        }}
      />
    </div>
  );
}

/* ── Sign-in history ─────────────────────────────────────────────────────────────────────────────── */

interface LoginEvent {
  id: string;
  event: 'SIGN_IN' | 'SIGN_OUT' | 'FAILED' | 'MFA_CHALLENGE' | 'MFA_PASSED' | 'LOCKED' | 'TOKEN_REFRESH';
  occurred_at: string;
  detail: Record<string, unknown>;
}

const LOGIN_WORD: Record<LoginEvent['event'], string> = {
  SIGN_IN: 'Signed in',
  SIGN_OUT: 'Signed out',
  FAILED: 'Failed attempt',
  MFA_CHALLENGE: 'Second factor requested',
  MFA_PASSED: 'Second factor passed',
  LOCKED: 'Account locked',
  TOKEN_REFRESH: 'Session refreshed',
};

function UserSessions({ entity }: { entity: User360 }) {
  const query = useQuery({
    queryKey: ['iam-login-activity', entity.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('login_activity')
        .select('id,event,occurred_at,detail')
        .eq('user_account_id', entity.id)
        .order('occurred_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return data as LoginEvent[];
    },
  });

  if (query.isLoading) return <LoadingRows rows={5} columns={3} />;
  if (query.isError) return <ErrorState error={query.error} retry={() => void query.refetch()} />;
  if ((query.data ?? []).length === 0) {
    return (
      <EmptyState
        title="No sign-in activity recorded"
        detail="Password attempts are reported to the database by the authentication service itself, so this list cannot be written to — or cleared — by the account it describes."
      />
    );
  }

  return (
    <ul className="divide-y divide-line">
      {query.data!.map((event) => {
        const bad = event.event === 'FAILED' || event.event === 'LOCKED';
        const refusedBecause = event.detail?.refused_because as string | undefined;
        return (
          <li key={event.id} className="flex h-[var(--height-row)] items-center gap-4 px-4">
            <span className="w-44 shrink-0 text-small text-ink-muted tabular">{fmtDateTime(event.occurred_at)}</span>
            <span className={cx('min-w-0 flex-1 truncate text-body', bad && 'font-medium text-refused')}>
              {LOGIN_WORD[event.event]}
              {refusedBecause ? <span className="font-normal text-ink-muted"> · refused because the account was {refusedBecause}</span> : null}
              {event.detail?.first_sign_in ? <span className="font-normal text-ink-muted"> · first sign-in, invitation accepted</span> : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/* ── The descriptor ──────────────────────────────────────────────────────────────────────────────── */

export const userDescriptor: Entity360Descriptor<User360> = {
  entityType: 'iam.user_accounts',
  kind: 'users',
  registerPath: '/admin/users',
  registerLabel: 'Users',

  load: (id) => rpc<User360>('user_360', { p_id: id }),
  title: (e) => e.name,
  code: (e) => e.email,

  status: (e) => {
    const meta = ACCOUNT_STATUS[e.status];
    return (
      <div className="flex items-center gap-2">
        {e.mfa_required ? <StatusBadge tone="info" icon={ShieldCheck}>MFA required</StatusBadge> : null}
        <StatusBadge tone={meta.tone} icon={meta.icon}>{meta.word}</StatusBadge>
      </div>
    );
  },

  summary: (e) => (
    <dl className="flex flex-wrap gap-x-8 gap-y-1">
      <div>
        <dt className="label-text">Roles</dt>
        <dd className={cx('tabular', e.counts.grants === 0 && e.status === 'ACTIVE' && 'font-medium text-refused')}>{e.counts.grants}</dd>
      </div>
      <div>
        <dt className="label-text">Applications</dt>
        <dd className="tabular">{e.counts.applications}</dd>
      </div>
      <div>
        <dt className="label-text">Last sign-in</dt>
        <dd>{e.last_sign_in_at ? fmtDate(e.last_sign_in_at) : '—'}</dd>
      </div>
      <div>
        <dt className="label-text">Failed attempts</dt>
        {/* null is "you may not see this", and must not read as a reassuring zero. */}
        <dd className={cx('tabular', (e.counts.failures ?? 0) > 0 && 'text-refused')}>{e.counts.failures ?? '—'}</dd>
      </div>
    </dl>
  ),

  rail: (entity) => (
    <div className="flex flex-col gap-4">
      <Panel>
        <PanelHeader title="Person" />
        {entity.person ? (
          <Link to={`/workforce/people/${entity.person.id}`} className="block px-4 py-3 hover:bg-brand-soft">
            <span className="block text-body">{entity.person.display_name}</span>
            <span className="block text-small text-ink-muted">Person record · {entity.person.status}</span>
          </Link>
        ) : (
          <p className="px-4 py-3 text-small text-ink-muted">
            Not linked to a person record. A login and a person are separate things; this one stands alone.
          </p>
        )}
      </Panel>
      {entity.invitation ? (
        <Panel>
          <PanelHeader title="Invitation" />
          <div className="px-4 py-3 text-small">
            <p>
              <span className="label-text">Status</span> {entity.invitation.status}
            </p>
            <p className="mt-1 text-ink-muted">
              {entity.invitation.status === 'PENDING' ? 'Expires' : 'Expired'} {fmtDate(entity.invitation.expires_at)}
            </p>
            {entity.invitation.proposed_roles.length > 0 ? (
              <p className="mt-1 text-ink-muted">Proposed: <span className="code-text">{entity.invitation.proposed_roles.join(', ')}</span></p>
            ) : null}
          </div>
        </Panel>
      ) : null}
    </div>
  ),

  tabs: [
    { id: 'overview', label: 'Overview', section: 'overview', render: (e) => <UserOverview entity={e} /> },
    { id: 'roles', label: 'Roles & scope', section: 'custom', count: (e) => e.counts.grants, render: (e) => <UserRoles entity={e} /> },
    { id: 'applications', label: 'Applications', section: 'custom', count: (e) => e.counts.applications, permission: 'iam.applications.view', render: (e) => <UserApplications entity={e} /> },
    {
      id: 'access',
      label: 'Effective access',
      section: 'custom',
      render: (e) => (
        <div>
          <EffectiveAccessView accountId={e.id} />
          <div className="border-t border-line">
            <div className="px-4 pt-3"><SectionLabel>Check one permission</SectionLabel></div>
            <AccessCheckPanel accountId={e.id} />
          </div>
        </div>
      ),
    },
    { id: 'sessions', label: 'Sign-in history', section: 'custom', count: (e) => e.counts.sign_ins ?? undefined, permission: 'iam.sessions.view', render: (e) => <UserSessions entity={e} /> },
    { id: 'activity', label: 'Activity', section: 'activity', count: (e) => e.counts.events ?? undefined },
    { id: 'audit', label: 'Audit', section: 'audit', permission: 'audit.view' },
  ],
};
